import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FpCorrelationSummaryStore } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-summary/services/fp-correlation-summary/fp-correlation-summary.store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CoordinateSystemsLookupService } from '@app/core/coordinate-systems/services/coordinate-systems-lookup.service';
import { coordinatesFieldLabel } from '@app/core/configs/coordinates-column.config';
import { LabelValuePresentationComponent } from '@app/shared/components/label-value-presentation/label-value-presentation.component';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { MeasurementSystem } from '@app/core/enums/measurement-system.enum';
import { map } from 'rxjs/operators';
import { LdCorrelationPipeProvidedIconComponent } from '@app/main/leak-detection/components/ld-helper/ld-correlation-pipe-provided-icon/ld-correlation-pipe-provided-icon.component';
import { LdCorrelationTypeIconWithQualityComponent } from '@app/main/leak-detection/components/ld-helper/ld-correlation-type-icon-with-quality/ld-correlation-type-icon-with-quality.component';
import { ElementWidthClassDirective } from '@app/shared/directives/element-width-class-directive';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { NgxPermissionsModule } from 'ngx-permissions';
import { PermissionName } from '@app/core/permissions/enums/permission-name.enum';
import { CommentCardComponent } from '@app/shared/components/comment/comment-card/comment-card.component';
import { FloatingPopupTabsService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-tabs.service';
import { FpCorrelationTabs } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/enums/fp-correlation-tabs.enum';
import { CommentCardNoDataComponent } from '@app/shared/components/comment/comment-card-no-data/comment-card-no-data.component';

@Component({
  selector: 'gc-fp-correlation-summary',
  standalone: true,
  imports: [
    TranslatePipe,
    LdCorrelationTypeIconWithQualityComponent,
    LabelValuePresentationComponent,
    LdCorrelationPipeProvidedIconComponent,
    ElementWidthClassDirective,
    NgxPermissionsModule,
    CommentCardComponent,
    CommentCardNoDataComponent,
  ],
  templateUrl: './fp-correlation-summary.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FpCorrelationSummaryComponent {
  public readonly currentTabStore = inject(FpCorrelationSummaryStore);
  protected readonly translateService = inject(TranslateService);
  protected readonly formattingData = inject(FormattingDataService);
  protected readonly coordinateSystems = inject(CoordinateSystemsLookupService);
  protected readonly floatingPopupService = inject(FloatingPopupService);
  protected readonly floatingPopupTabsService = inject(FloatingPopupTabsService);

  public popup = input<IFloatingPopupInstance>();

  public readonly data = toSignal(this.currentTabStore.data$, {
    initialValue: null,
  });

  public readonly correlation = computed(() => this.data()?.correlation);

  private readonly viewMeasurementSystem = toSignal(
    this.currentTabStore.viewMeasurementSystem$.pipe(map(s => s || MeasurementSystem.Metric)),
    { initialValue: MeasurementSystem.Metric }
  );

  public readonly logger1 = computed(() => {
    const logger = this.data()?.loggerDeployment1;

    return this.formattingData.formatSerialNo(logger?.serialNo);
  });

  public readonly logger2 = computed(() => {
    const logger = this.data()?.loggerDeployment2;

    return this.formattingData.formatSerialNo(logger?.serialNo);
  });

  public readonly soundSignalStartAt = computed(() =>
    this.formattingData.formatTime(this.correlation()?.soundSignalStartAt)
  );

  public readonly distanceL1 = computed<string>(() => {
    const correlation = this.correlation();

    return this.formattingData.formatLength(
      correlation?.distanceL1,
      this.viewMeasurementSystem(),
      !correlation?.pipeProvided
    );
  });

  public readonly distanceL2 = computed<string>(() => {
    const correlation = this.correlation();

    return this.formattingData.formatLength(
      correlation?.distanceL2,
      this.viewMeasurementSystem(),
      !correlation?.pipeProvided
    );
  });

  public readonly centerDistance = computed<string>(() => {
    const correlation = this.correlation();

    return this.formattingData.formatLength(correlation?.centerDistance, this.viewMeasurementSystem());
  });

  public readonly pipeLength = computed<string>(() => {
    const correlation = this.correlation();

    return this.formattingData.formatLength(correlation?.pipeLength, this.viewMeasurementSystem());
  });

  public readonly latitudeLongitude = computed<string>(() =>
    this.formattingData.formatCoordinates(this.correlation()?.coordinates)
  );

  public readonly coordinates = computed<string>(() =>
    this.formattingData.formatCustomCoordinates(this.correlation()?.customCoordinates)
  );

  public readonly coordinatesLabel = computed(() =>
    coordinatesFieldLabel(this.coordinateSystems.getFor(this.correlation()?.customCoordinates))
  );

  public readonly commentAuthor = computed<string>(
    () => this.correlation()?.comment?.author || this.formattingData.NO_DATA_VALUE_PLACEHOLDER_KEY
  );

  public readonly commentModifiedAt = computed<string>(() => {
    const modifiedAt = this.correlation()?.comment?.modifiedAt;
    if (!modifiedAt) {
      return this.formattingData.NO_DATA_VALUE_PLACEHOLDER_KEY;
    }

    return this.formattingData.formatDate(modifiedAt);
  });

  public readonly comment = computed<string>(
    () => this.correlation()?.comment?.content || this.formattingData.NO_DATA_VALUE_PLACEHOLDER_KEY
  );

  protected readonly PermissionName = PermissionName;

  public readonly consistency = computed(() => this.formattingData.formatNumber(this.correlation()?.consistency));
  public readonly maxTime = computed(() => this.formattingData.formatNumber(this.correlation()?.maxTime));
  public readonly maximum = computed(() => this.formattingData.formatNumber(this.correlation()?.maximum));
  public readonly soundVelocity = computed(() => this.formattingData.formatNumber(this.correlation()?.soundVelocity));
  public readonly overlap = computed(() => this.formattingData.formatNumber(this.correlation()?.overlap));
  public readonly corrLength = computed(() => this.formattingData.formatNumber(this.correlation()?.corrLength));
  public readonly delayMax = computed(() => this.formattingData.formatNumber(this.correlation()?.delayMax));
  public readonly whyUnsynchronized = computed(
    () => this.correlation()?.whyUnsynchronized ?? this.formattingData.NO_DATA_VALUE_PLACEHOLDER_KEY
  );

  public changeToCommentTab() {
    const popup = this.popup();

    if (popup) {
      if (popup.isMinimized) {
        this.floatingPopupService.maximize(popup.id);
      }

      this.floatingPopupTabsService.setActive(popup.id, FpCorrelationTabs.Comments);
    }
  }
}
